/**
 * The URL is the scope — ch05's half of the house pattern (`CLAUDE.md` §3.4).
 *
 * ── WHY THIS CHAPTER SUDDENLY HAS ONE ───────────────────────────────────────
 *
 * Every ch05 read until contract 0.23 was a collection over the whole book: plans,
 * cadence proposals, doors, lanes, lots, returns. None of them needed a parameter the
 * front end could not guess, so the chapter had no scope module and rightly so.
 *
 * 0.23 adds two reads that are inherently about **one record**:
 *
 *   `GET /exit/plans/{exit_plan_id}`                    one plan, fetched by its id
 *   `GET /exit/routing/{routing_decision_id}/routes`    the routes one decision weighed
 *
 * A list with no detail read makes a drill into one plan a re-page of the collection, and
 * a bookmarked link to a plan unresolvable — which is the platform's own argument for
 * shipping them. Honouring it means the identifier has to live somewhere shareable, and
 * G2.2 already decided where: *"any screen, filter set, drill path and scenario is a
 * shareable, bookmarkable link"*.
 *
 * ── THE FOUR RULES, AS THEY APPLY HERE ──────────────────────────────────────
 *
 * 1. **No default, ever.** Not "the first plan in the book", not "the decision at the top
 *    of the routing screen". A detail panel that opened on a record nobody chose is a
 *    record a planner will read as the one they were looking at.
 * 2. **`enabled: false` until the parameter is present.** No identifier, no request.
 * 3. **A designed state, not an empty panel.** "No plan selected" and "this plan id
 *    resolves to nothing" are different findings, and the second one is a decision —
 *    `read_plan` answers 404 for an out-of-scope plan *and* for one that does not exist,
 *    deliberately (G3.3: a refusal that confirmed the record exists would answer the
 *    question it refuses), so the screen must not claim to know which.
 * 4. **Parse from a string, not from `window`.** `readExitScope` takes a search string so
 *    the test asserts on a string, and the hook is the only thing that touches the global.
 *
 * Neither parameter is a new *shared* anything: both are query-string keys in this
 * chapter's own screens, and no shared file changes to add them.
 */

import { useEffect, useMemo, useState } from 'react';

/**
 * Which exit plan the detail panel resolves — `?exitPlanId=`.
 *
 * The plan's own identifier rather than the option key, because that is what
 * `GET /exit/plans/{exit_plan_id}` is keyed on. An option can carry more than one plan
 * across seasons, so an option-keyed link would be ambiguous at exactly the moment a
 * second season lands, and the ambiguity would resolve silently to whichever row sorted
 * first.
 */
export const EXIT_PLAN_ID_PARAM = 'exitPlanId';

/** Which routing decision's valuations are expanded — `?routingDecisionId=`. */
export const ROUTING_DECISION_ID_PARAM = 'routingDecisionId';

/**
 * The consolidation run the screen reads — `?runId=`.
 *
 * Never defaulted, under rule 1 above, and this REPLACES the `?asOf=` date the screen used
 * to resolve from `GET /exit/consolidation/doors/latest`. That exception did not survive
 * contact with production (2026-09-25): the job stamps a snapshot with the date its stock
 * was measured, not with the run, and a later run on an EARLIER stock date left the older
 * snapshot standing — so "the newest date" served an orphaned 09-06 position (a run that
 * made no decisions) over the 09-05 one the night's real cycle produced. The newest date is
 * not the newest run, and nothing on the screen could tell a planner which they were
 * reading. A run is the identity a consolidation is made under, so it is the perimeter; a
 * planner chooses one from the platform's run directory (`ConsolidationRunPicker`).
 */
export const CONSOLIDATION_RUN_ID_PARAM = 'runId';

/**
 * The option whose doors and lanes the consolidation screen reads — `?optionId=`.
 *
 * Never defaulted, under rule 1 above. The position is option × door, tens of thousands of
 * rows on one country's date, and consolidation is posed per option; a screen that opened
 * on the first option in the list would present one option's doors as though a planner had
 * chosen it.
 */
export const CONSOLIDATION_OPTION_PARAM = 'optionId';

export interface ExitScope {
  readonly exitPlanId: string | null;
  readonly routingDecisionId: string | null;
  readonly consolidationRunId: string | null;
  readonly consolidationOptionId: string | null;
}

/** One parameter, trimmed, with blank treated as absent. */
function readParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The scope a search string states. Pure: no `window`, no defaults, no guesses.
 *
 * `?exitPlanId=` present but empty is the same as absent — a link somebody truncated is
 * not a request for a plan whose id is the empty string, and issuing that request would
 * spend a 404 on a typo.
 */
export function readExitScope(search: string): ExitScope {
  const params = new URLSearchParams(search);
  return {
    exitPlanId: readParam(params, EXIT_PLAN_ID_PARAM),
    routingDecisionId: readParam(params, ROUTING_DECISION_ID_PARAM),
    consolidationRunId: readParam(params, CONSOLIDATION_RUN_ID_PARAM),
    consolidationOptionId: readParam(params, CONSOLIDATION_OPTION_PARAM),
  };
}

/**
 * Write one scope parameter onto the address bar, preserving everything else on it.
 *
 * `pushState` plus a manual `popstate`, which is load-bearing and not a workaround: the
 * shell navigates with `pushState` and the browser fires no event for it, so the hook
 * below would never learn that the scope changed. ch04's `navigateToScope` carries the
 * same note for the same reason.
 *
 * `null` removes the parameter rather than setting it empty, so closing a detail panel
 * leaves a link that is honestly unscoped instead of one carrying a blank key.
 *
 * `clear` names parameters removed in the same navigation — changing the consolidation run
 * clears `?optionId=`, because an option chosen under one run is not a choice made under
 * another, and one history entry keeps the back button on the pair the planner saw.
 */
export function setExitScopeParam(
  name: string,
  value: string | null,
  { replace = false, clear = [] }: { readonly replace?: boolean; readonly clear?: readonly string[] } = {},
): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  for (const cleared of clear) next.delete(cleared);
  if (value === null || value.trim() === '') next.delete(name);
  else next.set(name, value);
  const query = next.toString();
  const url = query === '' ? window.location.pathname : `?${query}`;
  // A REPLACE for a value the screen resolved rather than one the planner chose: the planner
  // arrived, they did not navigate, and a push would give the back button a URL nobody picked.
  if (replace) window.history.replaceState({}, '', url);
  else window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** The current scope, following browser back and forward. */
export function useExitScope(): ExitScope {
  const [, setNavigations] = useState(0);

  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const search = typeof window === 'undefined' ? '' : window.location.search;
  return useMemo(() => readExitScope(search), [search]);
}
